// Workaround for a fragno gap (t-hero-carousel-peek): fragno-carousel writes
// nothing to its light-DOM slides to say which one is active. Remove once
// fragno marks the active slide. Clone layout verified against fragno 1.17.0
// and 2.3.0 carousel.js.
//
// Marks the neighbors a peeking carousel shows beside its active slide: the
// most visible slide is the active one and every other slide gets
// data-peeked, which the hero section's CSS dims.
const THRESHOLDS = Array.from({ length: 11 }, (_, step) => step / 10);

// fragno-carousel appends two loop clones at each end: copies of slides N-2
// and N-1, then of 0 and 1. A clone and its original are one slide, so the
// jump that swaps them at a loop wrap changes no mark. Loop mode needs two or
// more slides, so every index below resolves; a lone slide has no clones.
function originalOf(slides: Element[]) {
  const originals = slides.filter((s) => !s.hasAttribute("data-loop-clone"));
  const clones = slides.filter((s) => s.hasAttribute("data-loop-clone"));
  const last = originals.length - 1;
  const copied = [last - 1, last, 0, 1].map((i) => originals[i]);

  return new Map([
    ...originals.map((s): [Element, Element] => [s, s]),
    ...clones.map((s, i): [Element, Element] => [s, copied[i]]),
  ]);
}

function markPeeked(carousel: Element, ratios: Map<Element, number>) {
  const slides = Array.from(carousel.children);
  const original = originalOf(slides);
  const visibility = new Map<Element, number>();
  slides.forEach((s) => {
    const key = original.get(s)!;
    visibility.set(key, Math.max(visibility.get(key) ?? 0, ratios.get(s) ?? 0));
  });
  const brightest = Math.max(...visibility.values());

  slides.forEach((s) =>
    s.toggleAttribute(
      "data-peeked",
      visibility.get(original.get(s)!)! < brightest,
    ),
  );
}

function watch(carousel: Element) {
  const ratios = new Map<Element, number>();
  let readyQueued = false;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, intersectionRatio }) =>
        ratios.set(target, intersectionRatio),
      );
      markPeeked(carousel, ratios);
      // data-peek-ready switches the dim transition on one painted frame after
      // the first marks, so the neighbors do not fade in on page load.
      if (!readyQueued) {
        readyQueued = true;
        requestAnimationFrame(() =>
          requestAnimationFrame(() =>
            carousel.setAttribute("data-peek-ready", ""),
          ),
        );
      }
    },
    // The carousel, not the viewport: page scroll must not read as a peek.
    { root: carousel, threshold: THRESHOLDS },
  );
  // The carousel appends its loop clones after connecting; observing an
  // element twice is a no-op, so every childList change re-walks the slides.
  const observeSlides = () =>
    Array.from(carousel.children).forEach((slide) => observer.observe(slide));

  observeSlides();
  new MutationObserver(observeSlides).observe(carousel, { childList: true });
}

export function initHeroCarouselPeek() {
  document.querySelectorAll("fragno-carousel[peek]").forEach(watch);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeroCarouselPeek);
} else {
  initHeroCarouselPeek();
}
